import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import useThemeStyles from '../../../theme/useThemeStyles';
import PriceTaxToggle from '../../Common/PriceTaxToggle';
import ProductPriceBreakdown from './ProductPriceBreakdown';

function ProductForm() {
  const _styles = useThemeStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'GBP',
    category: '',
    isOptional: false,
    taxPercentage: 0,
    priceIncludesTax: false
  });

  const businessId = selectedBusiness?.business?._id;

  useEffect(() => {
    if (isEdit && id && businessId) {
      api.get(`/api/products/${id}`)
        .then((res) => {
          const p = res.data;
          setFormData({
            name: p.name || '',
            description: p.description || '',
            price: p.price?.toString() || '',
            currency: p.currency || 'GBP',
            category: p.category || '',
            isOptional: p.isOptional || false,
            taxPercentage: p.taxPercentage ?? 0,
            priceIncludesTax: Boolean(p.priceIncludesTax)
          });
        })
        .catch(() => setError('Failed to load product'))
        .finally(() => setLoading(false));
    }
  }, [isEdit, id, businessId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      setError('Please enter a valid price');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/products/${id}`, {
          ...formData,
          price
        });
      } else {
        await api.post('/api/products', {
          businessId,
          ...formData,
          price
        });
      }
      navigate('/supplier/products');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        startIcon={<ArrowLeft size={18} />}
        onClick={() => navigate('/supplier/products')}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        {isEdit ? 'Edit Product' : 'Add Product'}
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            margin="normal"
          />
          <Box display="flex" gap={2} sx={{ mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label={
                (parseFloat(formData.taxPercentage) || 0) > 0
                  ? formData.priceIncludesTax
                    ? 'Price (incl. VAT)'
                    : 'Price (excl. VAT)'
                  : 'Price'
              }
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              required
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: '1 1 140px', minWidth: 120 }}
            />
            <Box sx={{ flex: '0 0 120px', minWidth: 110 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                VAT
              </Typography>
              <PriceTaxToggle
                value={formData.priceIncludesTax}
                onChange={(priceIncludesTax) => setFormData((prev) => ({ ...prev, priceIncludesTax }))}
              />
            </Box>
            <TextField
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              sx={{ width: 120 }}
            />
          </Box>
          <TextField
            fullWidth
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            placeholder="e.g. Photography, Albums"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Tax (%)"
            name="taxPercentage"
            type="number"
            value={formData.taxPercentage}
            onChange={handleChange}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            margin="normal"
          />
          <ProductPriceBreakdown
            variant="form"
            price={formData.price}
            taxPercentage={formData.taxPercentage}
            priceIncludesTax={formData.priceIncludesTax}
            currency={formData.currency}
          />
          <FormControlLabel
            control={
              <Switch
                name="isOptional"
                checked={formData.isOptional}
                onChange={handleChange}
              />
            }
            label="Optional upgrade (couple can select when accepting the agreement)"
            sx={{ mt: 2, display: 'block' }}
          />
          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button onClick={() => navigate('/supplier/products')}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : isEdit ? 'Save' : 'Add Product'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default ProductForm;
